/**
 * THE BOARD — the product opens on a question and the work in flight.
 *
 * NOT A DASHBOARD. No chart, no analytics, no activity feed, no counters of
 * things nobody asked about. One ruled line to write on, and the work grouped
 * by the state it is actually in.
 *
 * THE GROUPS ARE THE SERVER'S. Their order, their labels, their hints and
 * which work falls in which one are all decided in `@leveri/work-os` and sent
 * already grouped — an empty group never arrives, because a board of empty
 * rails is noise. This screen renders `groups[]` in the order it was given and
 * does not know that four of them exist.
 *
 * WHAT THIS SCREEN ASKS, AND WHY IT IS THE LEAST IT COULD ASK. Creating work
 * takes an instruction and the engagement it belongs to, and nothing else:
 * `POST /api/work` accepts exactly those two. The period, the currency, the
 * client and the control account are not asked here — three of them the
 * engagement already answers, and the fourth is a choice from the records
 * themselves, made inside the file once they are enclosed.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClientListResponse, EncloseRecordResponse, EngagementListResponse, WorkBoardResponse, WorkResponse,
  type Client, type Engagement, type WorkBoardResponse as Board, type WorkSummary,
} from "@leveri/contracts";
import {
  FileButton, GroupBand, InstructionLine, Notice, Reading, RuledList, RuledRow, Select, SERIF_OPSZ, StateText, markerTone,
} from "../file/index.js";
import { call, enclose, failureMessage } from "../api.js";
import { Desk } from "./shell.js";
import { useVocabularies } from "./vocabularies.js";
import { ACCEPT_RECORDS, RecordChoices, withChosen, type Chosen } from "./work/enclose.js";

export function BoardScreen() {
  const navigate = useNavigate();
  const [board, setBoard] = useState<Board | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      setBoard(await call("GET", "/api/work", WorkBoardResponse));
    } catch (error) {
      setFailure(failureMessage(error));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <Desk current="work">
      <section>
        <h1 className="font-serif text-[34px] font-medium leading-[1.15] tracking-[-0.015em] text-ink" style={SERIF_OPSZ}>
          What should Leveri work on?
        </h1>
        <p className="mt-2 text-[13px] text-stone">
          Say what the work is, in your own words, and name the engagement it belongs to. The records are enclosed in the
          file it opens, and nothing is started until you say so there.
        </p>
        <Composer onOpened={(work) => navigate(`/work/${work.id}`)} />
      </section>

      <section className="mt-12">
        <div className="flex items-baseline gap-3">
          <h2 className="text-[15px] font-semibold text-ink">Your work</h2>
          {board && board.total > 0 && (
            <span className="text-[13px] text-stone [font-variant-numeric:tabular-nums]">{board.total}</span>
          )}
        </div>

        {failure && (
          <div className="mt-4">
            <Notice>{failure}</Notice>
            <button type="button" onClick={() => void load()} className="mt-2 text-[13px] text-stone hover:text-ink">
              Try again
            </button>
          </div>
        )}

        {!board && !failure && <div className="mt-4"><Reading>Loading the work…</Reading></div>}

        {board && board.total === 0 && (
          <p className="mt-4 text-sm text-stone">No work yet. Say what the work is above and Leveri will open a file for it.</p>
        )}

        {board?.groups.map((group) => (
          <div key={group.key}>
            <GroupBand label={group.label} count={group.work.length} hint={group.hint} />
            <RuledList>
              {group.work.map((work) => <WorkRow key={work.id} work={work} onOpen={() => navigate(`/work/${work.id}`)} />)}
            </RuledList>
          </div>
        ))}
      </section>
    </Desk>
  );
}

/**
 * One piece of work on the board.
 *
 * Every word and every number here is the projection's: the title the
 * accountant wrote, the client and engagement the work is booked to, the
 * period as the engagement states it, and the status word with the tone the
 * server decided. Nothing is re-phrased and nothing is counted again.
 */
function WorkRow({ work, onOpen }: { work: WorkSummary; onOpen: () => void }) {
  return (
    <RuledRow as="button" onClick={onOpen} className="px-3">
      <div className="grid grid-cols-1 items-baseline gap-x-6 gap-y-1 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-ink">{work.title}</div>
          {/* The period as one phrase, read for a person on the server. The
              machine values stay on the wire and sit in the title, for anyone
              who needs to transcribe them. */}
          <div
            className="mt-0.5 text-[13px] text-stone [font-variant-numeric:tabular-nums]"
            title={`${work.periodStart} – ${work.cutOff}`}
          >
            {work.clientName} · {work.engagementTitle} · {work.periodText} · {work.currency}
          </div>
          {/* Counts, set as labelled numbers rather than as sentences: the
              server sent numbers, and a sentence built around one would be
              this screen stating something the projection did not. */}
          <div className="mt-0.5 text-[13px] text-stone [font-variant-numeric:tabular-nums]">
            Records {work.recordCount} · Open requests {work.openRequestCount}
            {work.workpaperVersion !== null && <> · Workpaper v{work.workpaperVersion}</>}
          </div>
        </div>
        <StateText tone={markerTone(work.tone)} className="text-[13px]">{work.statusLabel}</StateText>
      </div>
    </RuledRow>
  );
}

/**
 * THE COMPOSER — one ruled line to say what the work is, and the engagement it
 * belongs to.
 *
 * Deliberately not a form: the sentence comes first, and the only question
 * under it is the one the server genuinely cannot answer. The engagement is
 * chosen through its client because engagements are read per client — which is
 * also the order an accountant thinks in.
 *
 * THE RECORDS ARE NOT ENCLOSED HERE, and that is the API's shape rather than a
 * preference: a record is enclosed INTO a work
 * (`POST /api/work/:id/records`), and it must declare what kind of record it
 * is. The words for those kinds travel with the work file (`start.kinds`), so
 * the place that can ask the question is the file — which is where the work
 * lands the moment it is opened.
 *
 * The send control is disabled only while the line is empty, no engagement is
 * named, or a request is in flight. Whether the sentence SAYS enough is the
 * server's rule (`CreateWorkRequest`), and its refusal is shown as written: a
 * second copy of that rule here would be a second answer to the same question.
 */
function Composer({ onOpened }: { onOpened: (work: WorkSummary) => void }) {
  const [instruction, setInstruction] = useState("");
  /**
   * RECORDS ALONGSIDE THE INSTRUCTION (R5.1).
   *
   * They cannot be attached BEFORE the work: an enclosure names the work it is
   * enclosed in, and that anchor is the whole point of ADR-0009 — evidence
   * never arrives from nowhere. Alongside is feasible and is what a person
   * actually does: they have the files in front of them while they are saying
   * what the work is.
   *
   * So the work is opened first and the records are enclosed into it in the
   * same movement. Each kind is still DECLARED here, from the vocabulary the
   * server serves, and nothing is guessed from a filename.
   */
  const [chosen, setChosen] = useState<Chosen[]>([]);
  // The kinds of record, served — the browser holds no copy of the words.
  const { terms } = useVocabularies();
  const [clients, setClients] = useState<Client[] | null>(null);
  const [clientId, setClientId] = useState("");
  const [engagements, setEngagements] = useState<Engagement[] | null>(null);
  const [engagementId, setEngagementId] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let abandoned = false;
    call("GET", "/api/clients", ClientListResponse)
      .then((r) => { if (!abandoned) setClients(r.clients); })
      .catch((error: unknown) => { if (!abandoned) setFailure(failureMessage(error)); });
    return () => { abandoned = true; };
  }, []);

  // Engagements are this client's own, and are not asked for before there is a
  // client to ask about. A new client clears the engagement: an engagement
  // belongs to the client it was chosen under, and to nothing else.
  useEffect(() => {
    let abandoned = false;
    setEngagements(null);
    setEngagementId("");
    if (!clientId) return;
    call("GET", `/api/clients/${clientId}/engagements`, EngagementListResponse)
      .then((r) => { if (!abandoned) setEngagements(r.engagements); })
      .catch((error: unknown) => { if (!abandoned) setFailure(failureMessage(error)); });
    return () => { abandoned = true; };
  }, [clientId]);

  async function send() {
    if (busy || !instruction.trim() || !engagementId) return;
    setBusy(true);
    setFailure(null);
    try {
      const created = await call("POST", "/api/work", WorkResponse, { engagementId, instruction });
      if (chosen.length > 0) {
        await enclose(
          `/api/work/${created.work.id}/records`,
          EncloseRecordResponse,
          chosen.map((c) => c.file),
          chosen[0]!.role as string,
          chosen.map((c) => c.role as string)
        );
      }
      onOpened(created.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  const ready = Boolean(instruction.trim()) && Boolean(engagementId) && chosen.every((c) => c.role !== "");

  return (
    <div className="mt-5">
      <InstructionLine
        onSend={() => void send()}
        sendDisabled={busy || !ready}
        sendLabel="Open the work"
      >
        <label htmlFor="instruction" className="sr-only">What the work is</label>
        <textarea
          id="instruction"
          rows={1}
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void send(); }
          }}
          placeholder="Say what the work is…"
          className="block w-full resize-none border-0 bg-transparent px-0 py-1 text-[19px] leading-[1.35] text-ink placeholder:text-stone focus:outline-none"
        />
      </InstructionLine>

      {/* The one question under the line. Two short, semantically paired
          fields — the house maximum for a row, and they are genuinely one
          choice made in two steps. */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Select
          label="Client"
          value={clientId}
          disabled={clients === null || busy}
          onChange={(event) => setClientId(event.target.value)}
        >
          <option value="">{clients === null ? "Loading…" : "Choose a client"}</option>
          {(clients ?? []).map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
        </Select>
        <Select
          label="Engagement"
          value={engagementId}
          disabled={!clientId || engagements === null || busy}
          onChange={(event) => setEngagementId(event.target.value)}
          hint={clientId && engagements !== null && engagements.length === 0 ? "This client has no engagements yet." : undefined}
        >
          <option value="">{!clientId ? "Choose a client first" : engagements === null ? "Loading…" : "Choose an engagement"}</option>
          {(engagements ?? []).map((engagement) => (
            <option key={engagement.id} value={engagement.id}>
              {engagement.title} · {engagement.periodText}
            </option>
          ))}
        </Select>
      </div>

      {/* THE RECORDS, ATTACHED WHILE THE WORK IS BEING DESCRIBED. Each one
          says what it is before anything is sent. */}
      {terms?.recordKinds && terms.recordKinds.length > 0 && (
        <div className="mt-4">
          <RecordChoices chosen={chosen} kinds={terms.recordKinds} busy={busy} onChange={setChosen} />
          <FileButton
            accept={ACCEPT_RECORDS}
            multiple
            disabled={busy}
            onFiles={(files) => setChosen((held) => withChosen(held, files, []))}
          >
            {chosen.length === 0 ? "Attach records" : "Add more"}
          </FileButton>
          {chosen.length > 0 && !chosen.every((c) => c.role !== "") && (
            <span className="ml-3 text-[13px] text-stone">Say what each file is before opening the work.</span>
          )}
        </div>
      )}

      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
      {busy && <div className="mt-3"><Reading>{chosen.length > 0 ? "Opening the work and enclosing the records…" : "Opening the work…"}</Reading></div>}
    </div>
  );
}
